import * as React from "react"
import useAuth0Metadata from "api/useAuth0Metadata"
import config from "config"
import type { ControlDesk, Regions } from "types/region"
import type { ControlDeskSetting } from "types/auth"
import { useCustomerData } from "context/customer-provider"
import { getAllowedDesks, getRegionsByControlDesks, resolveDeskSelection } from "./helpers"

type Props = {
  children?: React.ReactNode
}

type ControlDeskContextType = {
  /**
   * The primary selected desk: the home desk when it's ticked. Drives
   * per-desk things (SRR email settings, default map framing) and the
   * single-desk consumers.
   */
  controlDesk: ControlDesk
  /** Every ticked desk (ART-3177 multi-desk): pages show their union. */
  controlDesks: ControlDesk[]
  /** Desks this user may tick: own desk + super-admin-granted desks (per desk, or per user), or all for ALL/??? accounts. */
  allowedDesks: ControlDesk[]
  /** The user's own desk (their row at this customer, ART-3182); null for accounts free to switch, or before it loads. */
  homeDesk: ControlDesk | null
  /** Select exactly this desk (legacy single-desk callers). */
  setControlDesk: (desk: ControlDesk) => void
  setControlDesks: (desks: ControlDesk[]) => void
  /** RR staff and accounts on ALL/???/no desk — may act on any desk (e.g. edit any desk's SRR emails). */
  userCanChangeDesk: boolean
  /** Union of the selected desks' regions. */
  regions: Regions
}

const universalDeskSelectors = ["ALL", "???"]
const defaultControlDesk: ControlDesk = "Scotland"

// The desk rule, pure so it is table-tested. `desk` is the account's control
// desk AT THE ACTIVE CUSTOMER (config.user_customer.control_desk via
// /me/customers, ART-3182 — it used to be the global Auth0 app_metadata, so
// a guest had none). On Network Rail a named desk pins the user to it; "ALL",
// the legacy "???" or no desk at all leave them free to switch (starting on
// the default desk), and RR staff can always switch. Elsewhere a stored desk
// is simply used, with no switching.
export const resolveControlDesk = ({
  customerId,
  desk,
  isSuperAdmin,
}: {
  customerId: string
  desk: ControlDeskSetting | null | undefined
  isSuperAdmin: boolean
}): { controlDesk: ControlDesk; userCanChangeDesk: boolean } => {
  const pinned = !!desk && !universalDeskSelectors.includes(desk)
  if (customerId === "NR") {
    const userCanChangeDesk = isSuperAdmin || !pinned
    return { controlDesk: userCanChangeDesk ? defaultControlDesk : (desk as ControlDesk), userCanChangeDesk }
  }
  return { controlDesk: pinned ? (desk as ControlDesk) : defaultControlDesk, userCanChangeDesk: false }
}

// initial context
const ControlDeskContext = React.createContext<ControlDeskContextType>({
  controlDesk: defaultControlDesk,
  controlDesks: [defaultControlDesk],
  allowedDesks: [],
  homeDesk: null,
  setControlDesk: () => undefined,
  setControlDesks: () => undefined,
  userCanChangeDesk: false,
  regions: [],
})

const ControlDeskProvider = ({ children }: Props) => {
  const { userIsSuperAdmin, userMetadata, updateUserMetadata } = useAuth0Metadata()
  const { customerId, customerData, availableCustomers, availableCustomersSettled } = useCustomerData()
  const desk = availableCustomers.find((customer) => customer.customer_id === customerId)?.control_desk ?? null

  // Nothing is decided until the account's settings at this customer are
  // known (until then: the default desk, as before).
  // (A boot snapshot's list counts here: the desk is derived on every render
  // and follows the server's list when it lands, and settling on the default
  // first would flip every NR page's desks a moment after it rendered.)
  const settled = (availableCustomersSettled || availableCustomers.length > 0) && !!customerId
  const resolved = resolveControlDesk({ customerId: customerId ?? "", desk, isSuperAdmin: !!userIsSuperAdmin })
  const userCanChangeDesk = settled && resolved.userCanChangeDesk
  // a pinned user's own desk; null for accounts free to switch (or not yet known)
  const homeDesk = settled && !resolved.userCanChangeDesk ? resolved.controlDesk : null

  const allowedDesks = React.useMemo(
    () =>
      settled
        ? getAllowedDesks({
            homeDesk,
            allDesks: userCanChangeDesk,
            everyDesk: config.controlDesks,
            deskVisibility: customerData?.deskVisibility,
            userDeskVisibility: customerData?.userDeskVisibility,
          })
        : [],
    [settled, homeDesk, userCanChangeDesk, customerData?.deskVisibility, customerData?.userDeskVisibility],
  )

  // This session's choice; until the user ticks anything, their last choice
  // (a UI preference) applies, then the home desk / the default desk.
  const [sessionSelection, setSessionSelection] = React.useState<ControlDesk[] | null>(null)
  const storedSelection = userMetadata?.selectedControlDesks
  const fallbackDesk = homeDesk ?? (userCanChangeDesk ? defaultControlDesk : allowedDesks[0]) ?? defaultControlDesk

  const controlDesks = React.useMemo(
    () => resolveDeskSelection(sessionSelection ?? storedSelection, allowedDesks, fallbackDesk),
    [sessionSelection, storedSelection, allowedDesks, fallbackDesk],
  )

  // Remembered per user (only meaningful when there's a choice to make),
  // debounced so a burst of ticks is one write and the last one wins.
  const persistTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  React.useEffect(() => () => clearTimeout(persistTimer.current), [])
  const setControlDesks = React.useCallback(
    (desks: ControlDesk[]) => {
      const next = resolveDeskSelection(desks, allowedDesks, fallbackDesk)
      setSessionSelection(next)
      if (allowedDesks.length > 1) {
        clearTimeout(persistTimer.current)
        persistTimer.current = setTimeout(() => updateUserMetadata({ selectedControlDesks: next }), 800)
      }
    },
    [allowedDesks, fallbackDesk, updateUserMetadata],
  )
  const setControlDesk = React.useCallback((desk: ControlDesk) => setControlDesks([desk]), [setControlDesks])

  const value = React.useMemo<ControlDeskContextType>(
    () => ({
      controlDesk: controlDesks[0],
      controlDesks,
      allowedDesks,
      homeDesk,
      setControlDesk,
      setControlDesks,
      userCanChangeDesk,
      regions: getRegionsByControlDesks(controlDesks),
    }),
    [controlDesks, allowedDesks, homeDesk, setControlDesk, setControlDesks, userCanChangeDesk],
  )

  return <ControlDeskContext.Provider value={value}>{children}</ControlDeskContext.Provider>
}

const useNRControlDesk = () => {
  const context = React.useContext(ControlDeskContext)

  if (context === undefined) {
    throw new Error("useNRControlDesk must be used within the ControlDeskProvider")
  }

  return context
}

export { ControlDeskProvider, useNRControlDesk }
